import { createPopper, detectOverflow, type Instance, type Modifier } from '@popperjs/core';
import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

import type { IDropDownWrapper } from '../Dropdown.helpers';

const VIEWPORT_PADDING = 10;
const MIN_DROPDOWN_HEIGHT = 120;

// Cap the panel to the space actually available in the viewport and make it scroll internally. Runs after
// computeStyles so it can augment the applied inline styles. This keeps every option reachable even when
// `disableReposition` turns off popper's flip/preventOverflow (so the panel can't move up or shift to fit)
// and works for both `absolute` and `fixed` strategies since detectOverflow measures against the viewport.
const constrainDropdownHeight: Modifier<'constrainDropdownHeight', object> = {
	name: 'constrainDropdownHeight',
	enabled: true,
	phase: 'beforeWrite',
	requires: ['computeStyles'],
	fn: ({ state }) => {
		const overflow = detectOverflow(state, { rootBoundary: 'viewport', padding: VIEWPORT_PADDING });
		const side = state.placement.startsWith('top') ? 'top' : 'bottom';
		const overshoot = overflow[side];

		if (overshoot > 0) {
			const available = Math.max(MIN_DROPDOWN_HEIGHT, state.rects.popper.height - overshoot);
			state.styles.popper.maxHeight = `${available}px`;
			state.styles.popper.overflowY = 'auto';
			state.elements.popper.classList.add('scrollbar');
		} else {
			state.elements.popper.classList.remove('scrollbar');
		}
	},
};

export const DropdownWrapper = ({
	children,
	dropDownRef,
	strategy = 'absolute',
	disableReposition = false,
}: IDropDownWrapper) => {
	const [visible, setVisible] = useState(false);
	const [dropdownStyles, setDropdownStyles] = useState<CSSProperties | undefined>(undefined);
	const [instance, setInstance] = useState<Instance | undefined>();

	const localButtonRef = useRef<HTMLButtonElement | null>(null);
	const buttonRef = dropDownRef ?? localButtonRef;
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const controlRef = useRef<HTMLDivElement | HTMLButtonElement | null>(null);

	useEffect(() => {
		let popperInstance: Instance | undefined;
		if (visible && controlRef.current && dropdownRef.current) {
			popperInstance = createPopper(controlRef.current, dropdownRef.current, {
				strategy,
				modifiers: [
					{
						name: 'offset',
						options: { offset: [0, 5] },
					},
					{
						name: 'preventOverflow',
						// Disabled when the caller opts out of overflow-driven repositioning.
						enabled: !disableReposition,
						options: {
							boundary: 'viewport',
							padding: 10,
						},
					},
					{
						name: 'flip',
						enabled: !disableReposition,
						options: {
							boundary: 'clippingParents',
							rootBoundary: 'document',
							fallbackPlacements: ['top-start', 'bottom-start', 'left-start'],
						},
					},
					{
						name: 'computeStyles',
						options: {
							adaptive: false,
							gpuAcceleration: false,
						},
					},
					constrainDropdownHeight,
				],
			});
			setInstance(popperInstance);
		}

		const handleClickOutside = (e: MouseEvent) => {
			const isControlRef =
				controlRef.current &&
				!controlRef.current.contains(e.target as Node) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node);

			const isDropdownRef =
				buttonRef.current &&
				!buttonRef.current.contains(e.target as Node) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node);

			if (isControlRef || isDropdownRef) {
				setVisible(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);

			if (popperInstance?.destroy !== undefined && typeof popperInstance.destroy === 'function') {
				popperInstance.destroy();
			}
		};
	}, [buttonRef, visible, strategy, disableReposition]);

	const handleVisibility = useCallback(() => {
		setVisible((v) => !v);
	}, []);

	const setVisibility = useCallback((value: boolean) => {
		setVisible(value);
	}, []);

	useEffect(() => {
		if (controlRef.current) {
			const rect = controlRef.current.getBoundingClientRect();
			// Honour the positioning strategy: with `strategy='fixed'` the popup must be `position: fixed`
			// too, otherwise a hardcoded `absolute` keeps it clipped by an overflow/transformed ancestor.
			const styles: CSSProperties = {
				position: strategy,
				width: rect.width,
			};
			setDropdownStyles(styles);
		}
	}, [visible, strategy]);

	useEffect(() => {
		const handleResize = () => {
			if (controlRef.current && visible) {
				const rect = controlRef.current.getBoundingClientRect();
				setDropdownStyles({
					...dropdownStyles,
					width: rect.width,
				});
			}
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [visible, dropdownStyles]);

	return (
		<div className='dropdown_wrapper'>
			{children?.({
				buttonRef,
				handleVisibility,
				setVisibility,
				visible,
				dropdownRef,
				controlRef,
				dropdownStyles,
				tipPlacement: instance?.state.placement ?? '',
			})}
		</div>
	);
};
